import DomErrorBoundary from 'components/errors/domError';
import PanoErrorBoundary from 'components/errors/panoError';
import { CONTROLS } from 'consts';
import World2d from 'containers/world/world2d';
import World3D from 'containers/world/world3d';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  setAutoRotate,
  // setGuideBoardVisible,
  setOpenPopUp,
  setPanning,
  toggleLanding,
  toggleMenu,
  updateWorldParams,
} from 'store/actions';

import { useWorldActions } from './hooks/useWorldActions';
import './styles.scss';

const CONFIG_DELAY = 10000;

export const WorldFC = (props) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const {
    json,
    tour,
    hotspotHistory,
    panoMode,
    viewMode,
    autoRotate,
    worldParams,
    isPanning,
    showWorld3D,
    menu,
  } = useSelector((state) => state);
  const { tourId, sceneId, hotspotId, groupId } = worldParams;

  const { scenes, customer } = json;
  const { isOpen: isNavOpen } = menu;

  // World States
  const [worldStates, setWorldStates] = useState({
    featuredHotspot: {},
    initiating: true,
    jsonInvalid: false,
    isPanning: false,
    sdLoaded: false,
    hdLoaded: false,
    spinner: true,
    showSpinner: true,
    showIntro: false,
    introStatus: 'in',
    defaultScene: null,
    hotspotHovering: false,
    popupOpen: false,
    shouldRotate: true,
    control: CONTROLS.ORBIT,
    isFinalIntroSlide: false,
    showPasscode: false,
    enteredPasscode: true,
    isDisableIntroLanding: true,
    cameraParams: {},
    sceneRotation: {},
  });
  const { currentPano, setCurrentPano, selectHotspot, selectSceneById } =
    useWorldActions();

  const panCounter = useRef(0);
  const disableRotate = useRef(false);
  const panTimer = useRef(null);
  const trackDeepLink = useRef(null);
  const { autoRotate: rotateCamera } = useSelector((state) => state);

  useEffect(() => {
    const setInitialScene = (groupId, sceneId) => {
      if (sceneId) {
        const selectedScene = scenes.find((sc) => sc._id === sceneId);

        if (selectedScene) {
          setCurrentPano(selectedScene);
          setWorldStates((prevStates) => {
            return {
              ...prevStates,
              defaultScene: selectedScene,
              initiating: false,
            };
          });
        }
      }
    };
    if (sceneId || hotspotId) {
      trackDeepLink.current = {
        tour: tourId,
        scene: sceneId,
        hotspot: hotspotId,
      };
      setInitialScene(groupId, sceneId);
    }
    dispatch(updateWorldParams({ tourId, groupId, sceneId, hotspotId }));
    // dispatch(setGuideBoardVisible(false));
    dispatch(setOpenPopUp(false));
    dispatch(toggleLanding(false));
  }, [dispatch, tourId, groupId, sceneId, hotspotId, scenes, setCurrentPano]);

  const resetTimer = useCallback(() => {
    if (panTimer.current) {
      clearTimeout(panTimer.current);
      panTimer.current = null;
    }
  }, []);

  const toggleRotate = useCallback(
    (isAutoRotate) => {
      if (!isAutoRotate) {
        disableRotate.current = false;
        resetTimer();
      }
      dispatch(
        setAutoRotate(
          isAutoRotate !== undefined ? isAutoRotate : !this.props.autoRotate
        )
      );
    },
    [dispatch, resetTimer]
  );

  const onAssetLoaded = useCallback((type) => {
    setWorldStates((prevStates) => {
      const newStates = { ...prevStates };
      newStates[`${type}`] = true;
      return newStates;
    });
  }, []);

  const pointerEvents = useMemo(() => {
    return {
      onClick: () => {
        if (worldStates.isNavOpen) {
          dispatch(toggleMenu(false));
        }
      },
      onPointerDown: () => {
        if (!worldStates.isPanning) {
          setWorldStates((prevStates) => {
            const newStates = { ...prevStates };
            newStates.isPanning = true;
            return newStates;
          });
        }
      },
      onPointerUp: () => {
        if (worldStates.isPanning) {
          if (isPanning) {
            dispatch(setPanning(false));
          }
          // this.isPanning = false;
          // this.setState({ isPanning: false });
          setWorldStates((prevStates) => {
            const newStates = { ...prevStates };
            newStates.isPanning = false;
            return newStates;
          });
          panCounter.current = 0;
          if (disableRotate.current && !panTimer.current) {
            panTimer.current = setTimeout(() => {
              toggleRotate(true);
              disableRotate.current = false;
              panTimer.current = null;
            }, CONFIG_DELAY);
          }
        }
      },
      onPointerMove: () => {
        if (worldStates.isPanning) {
          if (isPanning) {
            dispatch(setPanning(true));
          }
          if (isNavOpen) {
            dispatch(toggleMenu(false));
          }
          panCounter.current += 1;
          if (panCounter.current > 3) {
            if (autoRotate) {
              disableRotate.current = true;
              toggleRotate(false);
            }
            if (disableRotate.current) {
              resetTimer();
            }
          }
        }
      },
    };
  }, [
    worldStates.isNavOpen,
    worldStates.isPanning,
    dispatch,
    isPanning,
    toggleRotate,
    isNavOpen,
    autoRotate,
    resetTimer,
  ]);

  return (
    <div className="world-fc" style={{ flex: 1 }}>
      <PanoErrorBoundary>
        <World3D
          json={json}
          tour={tour}
          showSpinner={worldStates.spinner}
          showIntro={false}
          introClosed={
            !worldStates.spinner && worldStates.introStatus === 'out'
          }
          autoRotate={rotateCamera}
          customer={customer}
          panoMode={panoMode}
          isPanning={isPanning}
          currentPano={currentPano}
          onSDLoaded={() => onAssetLoaded('sdLoaded')}
          onHDLoaded={() => onAssetLoaded('hdLoaded')}
          onSwitchPano={(scene) => selectSceneById(scene.groupId, scene._id)}
          onSelectHotspot={selectHotspot}
          pointerEvents={pointerEvents}
          hotspotHistory={hotspotHistory}
          popupOpen={worldStates.popupOpen}
          setHotspotHovering={(isHovering) =>
            setWorldStates({ ...worldStates, hotspotHovering: isHovering })
          }
          shouldLimitPan={tour.shouldLimitPan}
          history={history}
          shouldRotate={worldStates.shouldRotate}
          viewMode={viewMode}
          setAutoRotate={(isAuto) => dispatch(setAutoRotate(isAuto))}
          showPasscode={worldStates.showPasscode}
          enteredPasscode={worldStates.enteredPasscode}
          isDisableIntroLanding={worldStates.isDisableIntroLanding}
          getCameraParams={(val) => {
            if (
              JSON.stringify(worldStates.cameraParams) !== JSON.stringify(val)
            ) {
              setWorldStates({ ...worldStates, cameraParams: val });
            }
          }}
          getSceneRotation={(val) =>
            setWorldStates({ ...worldStates, sceneRotation: val })
          }
        />
      </PanoErrorBoundary>

      <DomErrorBoundary>
        <World2d
          currentPano={currentPano}
          autoRotate={autoRotate}
          shouldRotate={worldStates.shouldRotate}
          showSpinner={worldStates.showSpinner}
          hotspotHistory={worldStates.hotspotHistory}
          toggleRotate={() => toggleRotate()}
          selectSceneById={selectSceneById}
          toggleMap={() => {}}
          toggleFeatured={() => {}}
          introStatus={worldStates.introStatus}
          onClickPoweredBy={() => {}}
          showIntro={false}
          history={history}
          cameraParams={worldStates.cameraParams}
          sceneRotation={worldStates.sceneRotation}
          showWorld3D={showWorld3D}
        />
      </DomErrorBoundary>
    </div>
  );
};
